import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useAuthContext } from 'src/auth/hooks';
import { GET_ONBOARDING_STATE } from 'src/api/queries/getOnboardingState';
import {
  GET_APP_BOOTSTRAP,
  BootstrapCompany,
  BootstrapOnboardingState,
  BootstrapShopifyPermissionStatus,
} from 'src/api/queries/getAppBootstrap';
import { UPDATE_SELECTED_COMPANY } from 'src/api/mutations/updateSelectedCompany';
import {
  Company,
  setSelectedCompany as setSelectedCompanyInStorage,
  getSelectedCompany,
  clearSelectedCompany,
} from 'src/utils/company-storage';
import { getStepPath } from 'src/constants/onboarding';
import { GeneralSettingsType } from 'src/types/settings';
import {
  fetchCompanySettings,
  getStoredCompanySettings,
  updateCompanySetting,
  storeCompanySettings,
} from 'src/services/companySettings';

// ----------------------------------------------------------------------

export interface AppBootstrapState {
  // Company data
  companies: Company[];
  selectedCompany: Company | null;

  // Company settings
  companySettings: GeneralSettingsType | null;
  isLoadingSettings: boolean;
  settingsError: string | null;

  // Loading states
  isLoadingCompanies: boolean;

  // Status
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;

  // Validation state (for optimistic UI pattern)
  isValidating: boolean;
  validationComplete: boolean;

  // Bootstrap data (from unified query)
  onboardingState: BootstrapOnboardingState | null;
  subscriptionValid: boolean | null;
  shopifyPermissionStatus: BootstrapShopifyPermissionStatus | null;
}

interface AppBootstrapContextValue extends AppBootstrapState {
  // Company actions
  selectCompany: (
    company: Company,
    options?: { onNavigationNeeded?: (redirectPath: string) => void }
  ) => Promise<void>;

  // Settings actions
  refreshSettings: () => Promise<void>;
  updateSetting: <K extends keyof GeneralSettingsType>(
    key: K,
    value: GeneralSettingsType[K]
  ) => void;
  isSettingsReady: boolean;
}

const AppBootstrapContext = createContext<AppBootstrapContextValue | null>(null);

// ----------------------------------------------------------------------

interface AppBootstrapProviderProps {
  children: ReactNode;
}

/**
 * Handles app-level data bootstrapping:
 * - Fetches user's companies when authenticated
 * - Auto-selects company if only one available
 * - Manages company selection state
 * - Provides loading states for policy decisions
 *
 * This replaces the data fetching logic previously in CompanyContextGuard
 */
export function AppBootstrapProvider({ children }: AppBootstrapProviderProps) {
  const { authenticated, loading: authLoading, currentUser } = useAuthContext();
  const apolloClient = useApolloClient();
  const [updateSelectedCompanyMutation] = useMutation(UPDATE_SELECTED_COMPANY);

  // Initialize state with cached data for optimistic UI
  const cachedCompany = getSelectedCompany();
  const cachedSettings = getStoredCompanySettings();

  const [state, setState] = useState<AppBootstrapState>({
    companies: cachedCompany ? [cachedCompany] : [], // Assume cached company is valid initially
    selectedCompany: cachedCompany,
    companySettings: cachedSettings,
    isLoadingSettings: false,
    settingsError: null,
    isLoadingCompanies: false,
    // If we have cached data, start as 'ready' for instant UI
    status: cachedCompany ? 'ready' : 'idle',
    error: null,
    // Validation state
    isValidating: false,
    validationComplete: false,
    // Bootstrap data (will be populated by validation)
    onboardingState: null,
    subscriptionValid: null,
    shopifyPermissionStatus: null,
  });

  // Prevent multiple concurrent fetches (ref avoids causing effect dependency churn)
  const isFetchingCompaniesRef = useRef(false);
  const isFetchingSettingsRef = useRef(false);

  // Ref to access current state without causing callback recreation
  const stateRef = useRef(state);
  stateRef.current = state;

  const selectCompany = useCallback(
    async (company: Company, options?: { onNavigationNeeded?: (redirectPath: string) => void }) => {
      const previousCompany = state.selectedCompany;

      try {
        // Batch all state updates and external operations to minimize re-renders

        // 1. Update local state and localStorage immediately
        setState((prev) => ({
          ...prev,
          selectedCompany: company,
        }));
        setSelectedCompanyInStorage(company);

        // 2. Update Cognito asynchronously (fire and forget for better UX)
        updateSelectedCompanyMutation({
          variables: { companyId: company.id },
        }).catch((error) => {
          console.warn('Error updating Cognito companyId:', error);
        });

        // 3. Fetch settings before cache reset to minimize loading states
        let newSettings = null;
        if (!isFetchingSettingsRef.current) {
          try {
            isFetchingSettingsRef.current = true;
            newSettings = await fetchCompanySettings(company.id, apolloClient, 'company-switch');
          } catch (settingsError) {
            console.warn('Error fetching settings after company switch:', settingsError);
          } finally {
            isFetchingSettingsRef.current = false;
          }
        }

        // 4. Reset Apollo cache to refetch all queries with new company context
        await apolloClient.resetStore();

        // 5. Update settings state if we fetched them
        if (newSettings) {
          setState((prev) => ({
            ...prev,
            companySettings: newSettings,
            isLoadingSettings: false,
            settingsError: null,
          }));
        }

        // 6. Handle onboarding navigation if needed
        if (options?.onNavigationNeeded) {
          try {
            const onboardingResult = await apolloClient.query({
              query: GET_ONBOARDING_STATE,
              fetchPolicy: 'network-only',
            });

            const onboardingState = onboardingResult.data?.getOnboardingState;
            if (onboardingState?.isNew) {
              const redirectPath = getStepPath(onboardingState.currentStep);
              options.onNavigationNeeded(redirectPath);
            }
          } catch (onboardingError) {
            console.warn('Error checking onboarding state after company switch:', onboardingError);
          }
        }
      } catch (error) {
        console.error('Error during company switch:', error);

        // Rollback state on failure
        setState((prev) => ({
          ...prev,
          selectedCompany: previousCompany,
        }));

        // Rollback localStorage
        if (previousCompany) {
          setSelectedCompanyInStorage(previousCompany);
        } else {
          clearSelectedCompany();
        }

        // Re-throw to let the UI handle the error
        throw new Error(
          `Failed to switch company: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    [apolloClient, updateSelectedCompanyMutation, state.selectedCompany]
  );

  // Company settings management
  const fetchSettings = useCallback(async () => {
    if (!state.selectedCompany?.id || isFetchingSettingsRef.current) {
      return;
    }

    isFetchingSettingsRef.current = true;
    setState((prev) => ({
      ...prev,
      isLoadingSettings: true,
      settingsError: null,
    }));

    try {
      const settings = await fetchCompanySettings(
        state.selectedCompany.id,
        apolloClient,
        'unified-bootstrap'
      );
      setState((prev) => ({
        ...prev,
        companySettings: settings,
        isLoadingSettings: false,
        settingsError: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load settings';
      setState((prev) => ({
        ...prev,
        isLoadingSettings: false,
        settingsError: errorMessage,
      }));
    } finally {
      isFetchingSettingsRef.current = false;
    }
  }, [state.selectedCompany?.id, apolloClient]);

  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  const updateSetting = useCallback(
    <K extends keyof GeneralSettingsType>(key: K, value: GeneralSettingsType[K]) => {
      if (!state.companySettings) return;

      // Update local state immediately for responsiveness
      setState((prev) => ({
        ...prev,
        companySettings: prev.companySettings ? { ...prev.companySettings, [key]: value } : null,
      }));

      // Update in localStorage
      updateCompanySetting(key, value);
    },
    [state.companySettings]
  );

  const isSettingsReady = Boolean(state.companySettings);

  // Smart company selection logic
  const initializeCompanySelection = useCallback(
    (companies: Company[]) => {
      const storedCompany = getSelectedCompany();

      // Validate stored company still exists in user's available companies
      const validStoredCompany = storedCompany && companies.find((c) => c.id === storedCompany.id);

      if (validStoredCompany) {
        return validStoredCompany;
      }

      // Clear invalid stored company if it exists
      if (storedCompany) {
        clearSelectedCompany();
      }

      // Auto-select if only one company available (regardless of previous stored company)
      if (companies.length === 1) {
        const autoSelected = companies[0];
        setSelectedCompanyInStorage(autoSelected);

        // Update Cognito asynchronously (fire and forget for better UX)
        updateSelectedCompanyMutation({
          variables: { companyId: autoSelected.id },
        }).catch((error) => {
          console.warn('Error updating Cognito companyId during auto-selection:', error);
        });

        return autoSelected;
      }

      return null; // Force selection UI
    },
    [updateSelectedCompanyMutation]
  );

  // Unified bootstrap function - replaces the old fetchCompanies with a single query
  const fetchWithBootstrap = useCallback(async () => {
    // Don't act while auth is still loading - preserve existing state
    if (authLoading) {
      return;
    }

    if (!authenticated) {
      // Only clear localStorage when definitively unauthenticated (not just loading)
      clearSelectedCompany();
      isFetchingCompaniesRef.current = false;
      setState((prev) => ({
        ...prev,
        companies: [],
        selectedCompany: null,
        status: 'ready',
        isLoadingCompanies: false,
        companySettings: null,
        isLoadingSettings: false,
        settingsError: null,
        isValidating: false,
        validationComplete: false,
        onboardingState: null,
        subscriptionValid: null,
        shopifyPermissionStatus: null,
      }));
      return;
    }

    // Prevent multiple concurrent fetches
    if (isFetchingCompaniesRef.current) {
      return;
    }

    // If we have cached data, show it immediately and don't show loading state
    // Use ref to avoid adding state to dependencies (which would cause infinite loop)
    const currentState = stateRef.current;
    const hasCachedData = currentState.selectedCompany && currentState.companySettings;

    isFetchingCompaniesRef.current = true;

    // Only show loading state if we don't have cached data
    if (!hasCachedData) {
      setState((prev) => ({
        ...prev,
        isLoadingCompanies: true,
        status: 'loading',
        error: null,
      }));
    } else {
      setState((prev) => ({ ...prev, isValidating: true }));
    }

    try {
      // Single unified query - replaces GET_COMPANIES_FOR_USER + GET_COMPANY_SETTINGS + others
      const { data } = await apolloClient.query({
        query: GET_APP_BOOTSTRAP,
        variables: { companyId: currentState.selectedCompany?.id },
        fetchPolicy: 'network-only',
      });

      const bootstrap = data?.getAppBootstrap;
      if (!bootstrap) {
        throw new Error('Bootstrap query returned no data');
      }

      // Convert bootstrap companies to Company type. Keep bootstrapProgress so
      // the company selector can show per-workspace readiness without a refetch.
      const companies: Company[] = bootstrap.companies.map((c: BootstrapCompany) => ({
        id: c.id,
        companyName: c.companyName,
        bootstrapProgress: c.bootstrapProgress,
      }));

      // Determine final selected company
      let finalSelectedCompany = currentState.selectedCompany;

      // Check if current selection is still valid
      const isCurrentSelectionValid =
        currentState.selectedCompany &&
        companies.find((c: Company) => c.id === currentState.selectedCompany!.id);

      if (!isCurrentSelectionValid) {
        // Current selection is invalid or missing, use smart selection
        finalSelectedCompany = initializeCompanySelection(companies);
      }

      // Build settings from bootstrap data
      const newSettings =
        bootstrap.currencyCode !== null && finalSelectedCompany
          ? {
              currencyCode: bootstrap.currencyCode || 'USD',
              defaultSafetyStockWeeks: bootstrap.defaultSafetyStockWeeks ?? 2,
              fallbackSupplierDeliveryTime: bootstrap.fallbackSupplierDeliveryTime ?? 14,
              warningThreshold: bootstrap.warningThreshold ?? 20,
              preferredMetric: bootstrap.preferredMetric || 'units',
              timestamp: Date.now(),
              source: 'bootstrap',
            }
          : null;

      // Store settings in localStorage if we got new data
      if (newSettings) {
        storeCompanySettings(newSettings);
      }

      setState((prev) => ({
        ...prev,
        companies,
        selectedCompany: finalSelectedCompany,
        isLoadingCompanies: false,
        status: 'ready',
        error: null,
        isValidating: false,
        validationComplete: true,
        // Bootstrap data for use-app-readiness (avoids separate queries)
        onboardingState: bootstrap.onboardingState,
        subscriptionValid: bootstrap.subscriptionValid,
        shopifyPermissionStatus: bootstrap.shopifyPermissionStatus,
        // Update settings
        companySettings: newSettings || prev.companySettings,
        isLoadingSettings: false,
        settingsError: null,
      }));
    } catch (error) {
      console.error('[AppBootstrapProvider] Error in bootstrap:', error);

      // If we have cached data, mark validation complete but keep cached data
      if (hasCachedData) {
        setState((prev) => ({
          ...prev,
          isValidating: false,
          validationComplete: true,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          companies: [],
          selectedCompany: null,
          isLoadingCompanies: false,
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to load',
        }));
      }
    } finally {
      isFetchingCompaniesRef.current = false;
    }
  }, [authenticated, authLoading, apolloClient, initializeCompanySelection]);

  // Bootstrap when auth state changes (or on mount)
  useEffect(() => {
    fetchWithBootstrap();
  }, [fetchWithBootstrap]);

  // Update Sentry user context when authentication state or selected company changes
  useEffect(() => {
    if (authenticated && currentUser && state.selectedCompany) {
      Sentry.setUser({
        id: currentUser.id,
        email: currentUser.email,
        // Add company_id as a custom attribute for better error tracking
        company_id: state.selectedCompany.id,
        company_name: state.selectedCompany.companyName,
      });
    } else if (!authenticated) {
      // Clear user context when logged out
      Sentry.setUser(null);
    }
  }, [authenticated, currentUser, state.selectedCompany]);

  const contextValue: AppBootstrapContextValue = useMemo(
    () => ({
      ...state,
      selectCompany,
      refreshSettings,
      updateSetting,
      isSettingsReady,
    }),
    [state, selectCompany, refreshSettings, updateSetting, isSettingsReady]
  );

  return (
    <AppBootstrapContext.Provider value={contextValue}>{children}</AppBootstrapContext.Provider>
  );
}

// ----------------------------------------------------------------------

export function useAppBootstrap(): AppBootstrapContextValue {
  const context = useContext(AppBootstrapContext);
  if (!context) {
    throw new Error('useAppBootstrap must be used within AppBootstrapProvider');
  }
  return context;
}

// ----------------------------------------------------------------------

/**
 * Compatibility hook for legacy CompanySettingsProvider
 * Provides same interface as useCompanySettings()
 */
export function useCompanySettings() {
  const {
    companySettings: settings,
    isLoadingSettings: loading,
    settingsError,
    refreshSettings,
    updateSetting,
    isSettingsReady: isReady,
  } = useAppBootstrap();

  return {
    settings,
    loading,
    error: settingsError ? new Error(settingsError) : null,
    refreshSettings,
    updateSetting,
    isReady,
  };
}
